import { useEffect, useId, useRef, useState } from 'react';
import { Languages } from 'lucide-react';
import { TL4KHeaderButton } from '../components/TL4KHeaderButton';
import { colors, shadows, typography } from '../design-tokens';
import { STUDENT_LANGUAGES, getStudentLanguage, type StudentLanguageCode } from './languages';

export interface StudentLanguagePickerProps {
  value: StudentLanguageCode | null;
  onChange: (next: StudentLanguageCode | null) => void;
}

/**
 * Scelta della lingua madre per l'aiuto alla lettura (pulsante dell'header + elenco in pagina).
 *
 * Ogni lingua è scritta nella propria scrittura: il bambino deve riconoscerla senza leggere l'italiano.
 * Il nome italiano accanto serve al docente.
 */
export function StudentLanguagePicker({ value, onChange }: StudentLanguagePickerProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const listId = useId();
  const current = value ? getStudentLanguage(value) : null;

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current && event.target instanceof Node && !rootRef.current.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const choose = (next: StudentLanguageCode | null) => {
    onChange(next);
    setOpen(false);
  };

  const optionStyle = (selected: boolean) => ({
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 12,
    width: '100%',
    padding: '8px 12px',
    border: 'none',
    borderRadius: 'var(--tl4k-border-radius-sm, 6px)',
    background: selected ? `var(--tl4k-primary-50, ${colors.primary[50]})` : 'transparent',
    color: selected ? `var(--tl4k-primary-700, ${colors.primary[700]})` : `var(--tl4k-neutral-800, ${colors.neutral[800]})`,
    fontFamily: `var(--tl4k-font-family-body, ${typography.fontFamilyBody})`,
    textAlign: 'start' as const,
    cursor: 'pointer',
    textTransform: 'none' as const,
  });

  return (
    <div ref={rootRef} style={{ position: 'relative', flexShrink: 0 }}>
      <TL4KHeaderButton
        variant="neutral"
        active={!!current}
        icon={Languages}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label="Lingua per l'aiuto alla lettura"
        title="La mia lingua"
        onClick={() => setOpen((v) => !v)}
      >
        {current ? <span lang={current.code} dir={current.dir} style={{ textTransform: 'none' }}>{current.nativeName}</span> : null}
      </TL4KHeaderButton>
      {open && (
        <div
          id={listId}
          role="listbox"
          aria-label="La mia lingua"
          data-tl4k-i18n-skip=""
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            zIndex: 1000,
            minWidth: 220,
            padding: 6,
            borderRadius: 'var(--tl4k-border-radius-md, 10px)',
            border: `1px solid var(--tl4k-neutral-200, ${colors.neutral[200]})`,
            background: `var(--tl4k-surface-default, ${colors.surface.default})`,
            boxShadow: `var(--tl4k-shadow-lg, ${shadows.lg})`,
          }}
        >
          <button type="button" role="option" aria-selected={!current} style={optionStyle(!current)} onClick={() => choose(null)}>
            <span lang="it">Solo italiano</span>
          </button>
          {STUDENT_LANGUAGES.map((l) => (
            <button
              key={l.code}
              type="button"
              role="option"
              aria-selected={value === l.code}
              style={optionStyle(value === l.code)}
              onClick={() => choose(l.code)}
            >
              <span lang={l.code} dir={l.dir} style={{ fontSize: '1.05rem', fontWeight: 600 }}>
                {l.nativeName}
              </span>
              <span lang="it" style={{ fontSize: '0.8rem', color: `var(--tl4k-neutral-500, ${colors.neutral[500]})` }}>
                {l.italianName}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
